import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

// Import the filter primitives DIRECTLY (not via the barrel) so the heavy
// `<DateRangeFilter>` → `@mui/x-date-pickers` dep the barrel re-exports
// isn't pulled into the routed bundle — the users page has no date filter.
import { FilterBar } from '../../../components/filters/FilterBar';
import { SearchInput } from '../../../components/filters/SearchInput';
import { StatusMultiSelect } from '../../../components/filters/StatusMultiSelect';
import {
  KYC_OPTIONS,
  MIN_SEARCH_LENGTH,
  ROLE_OPTIONS,
  STATUS_OPTIONS,
  type UserFilters,
} from '../_lib/userFilters';

/**
 * Filter toolbar for the users list (AUT-124) — search + role + status +
 * KYC. Extracted from the page so the page stays under the line cap.
 * Every value is URL-state-driven via the parent's `setFilters` patch.
 */
export interface UsersFilterBarProps {
  filters: UserFilters;
  setFilters: (
    patch: Partial<Record<keyof UserFilters, string | number | string[] | null>>,
  ) => void;
  canClear: boolean;
  onClear: () => void;
}

export const UsersFilterBar = ({
  filters,
  setFilters,
  canClear,
  onClear,
}: UsersFilterBarProps): ReactElement => {
  const { t } = useTranslation();
  return (
    <FilterBar canClear={canClear} onClear={onClear}>
      <SearchInput
        initialValue={filters.q ?? ''}
        placeholder={t('users.search.placeholder')}
        minLength={MIN_SEARCH_LENGTH}
        onSearch={(q) => setFilters({ q: q || null })}
      />
      <TextField
        select
        size="small"
        label={t('users.filters.roleLabel')}
        value={filters.role ?? ''}
        onChange={(e) => setFilters({ role: e.target.value || null })}
        sx={{ minWidth: 160 }}
      >
        <MenuItem value="">{t('users.filters.roleAll')}</MenuItem>
        {ROLE_OPTIONS.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {t(o.labelKey)}
          </MenuItem>
        ))}
      </TextField>
      <StatusMultiSelect
        status={filters.status ?? []}
        options={STATUS_OPTIONS}
        label={t('users.filters.statusLabel')}
        onChange={(next) => setFilters({ status: next })}
      />
      <TextField
        select
        size="small"
        label={t('users.filters.kycLabel')}
        value={filters.kycStatus ?? ''}
        onChange={(e) => setFilters({ kycStatus: e.target.value || null })}
        sx={{ minWidth: 170 }}
      >
        <MenuItem value="">{t('users.filters.kycAll')}</MenuItem>
        {KYC_OPTIONS.map((o) => (
          <MenuItem key={o.value} value={o.value}>
            {t(o.labelKey)}
          </MenuItem>
        ))}
      </TextField>
    </FilterBar>
  );
};
